import { useState, useLayoutEffect, useEffect } from 'react';
import { ActionButton } from '../components/Button';
import Path from '../components/Link/Path';
import { useNavigate } from 'react-router';
import { BuildingOffice2, LockClosed, LockOpen } from '../components/svg';
import { Input, SelectInput, SwitchStatus } from '../components/Input';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import { useAppDispatch, useAppSelector } from '../utils/redux';
import { SelectChangeEvent } from '@mui/material/Select';
import validator from 'validator';
import {
  toggleCompanySupport,
  updateCompany,
} from '../redux/actions/companyAction';
import { isAdminSelector } from '../redux/selectors/user';
import {
  companySelector,
  companySupportSelector,
} from '../redux/selectors/company';
import ColorType from '../components/Theme/ColorType';
import { useTranslation } from 'react-i18next';
import { setAxiosHeaders } from '../utils/helper';
import { Text } from '../components/Text';

type ErrorProp = {
  companyName?: string;
  companyAddress?: string;
  status?: string;
  valve?: string;
  mqttHost?: string;
  mqttPort?: string;
  mqttTopic?: string;
};

const Statuses = [
  { value: '1', label: 'Active' },
  { value: '0', label: 'Inactive' },
];

const Publishs = [
  { value: true, label: 'Active' },
  { value: false, label: 'Inactive' },
];

const StatusesValve = [
  { value: '1', label: 'Yes' },
  { value: '0', label: 'No' },
];

const StatusesValveFR = [
  { value: '1', label: 'Oui' },
  { value: '0', label: 'Non' },
];

const CompanyDetails = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();
  const [companyId, setCompanyId] = useState(0);
  const [companyName, setCompanyName] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [status, setStatus] = useState('1');
  const [valve, setValve] = useState('1');
  const [mqttHost, setHost] = useState('');
  const [mqttPort, setPort] = useState<number | null>(null);
  const [mqttTopic, setTopic] = useState('');
  const [mqttPublish, setPublish] = useState(false);
  const [errors, setErrors] = useState<ErrorProp>({});
  const isAdmin = useAppSelector(isAdminSelector);
  const company = useAppSelector(companySelector);
  const companySupport = useAppSelector(companySupportSelector);

  const activeLocale = i18n.resolvedLanguage;

  useEffect(() => {
    setAxiosHeaders();
  }, []);

  const isUpdated =
    company?.name === companyName &&
    company?.address === companyAddress &&
    // eslint-disable-next-line prettier/prettier
    (isAdmin && status === Number(company?.enabled).toString()) &&
    isAdmin &&
    mqttHost === company?.mqtt_host &&
    isAdmin &&
    mqttPort === company?.mqtt_port &&
    isAdmin &&
    mqttTopic === company?.mqtt_topic &&
    isAdmin &&
    mqttPublish === company?.mqtt_publish_enabled &&
    // eslint-disable-next-line prettier/prettier
    (isAdmin && valve === Number(company?.prevent_valve_reopen_on_leak).toString());

  useLayoutEffect(() => {
    if (company) {
      setCompanyId(company?.id);
      setCompanyName(company?.name);
      company?.address && setCompanyAddress(company.address);
      isAdmin && setStatus(Number(company?.enabled).toString());
      isAdmin &&
        setValve(Number(company?.prevent_valve_reopen_on_leak).toString());
      isAdmin && setHost(company?.mqtt_host);
      isAdmin && setPort(company?.mqtt_port);
      isAdmin && setTopic(company?.mqtt_topic);
      isAdmin && setPublish(company?.mqtt_publish_enabled);
    }
    return () => {
      setErrors({});
      setCompanyName('');
      setCompanyAddress('');
      setStatus(Statuses[0].value);
      setValve(Statuses[0].value);
      setHost('');
      setPort(null);
      setTopic('');
      setPublish(false);
    };
  }, []);

  const handleChangeName = (text: string) => {
    setCompanyName(text);
  };

  const handleChangeAddress = (text: string) => {
    setCompanyAddress(text);
  };

  const handleChangeStatus = (event: SelectChangeEvent) => {
    setStatus(event.target.value);
  };

  const handleChangeValve = (event: SelectChangeEvent) => {
    setValve(event.target.value);
  };

  const handleChangeHost = (text: string) => {
    setHost(text);
  };

  const handleChangePort = (text: string) => {
    setPort(Number(text));
  };

  const handleChangeTopic = (text: string) => {
    setTopic(text);
  };

  const validateData = () => {
    const errors: ErrorProp = {};
    if (validator.isEmpty(companyName)) {
      errors.companyName = 'A valid company name is required';
    }
    return errors;
  };

  const handleSendData = () => {
    const errors = validateData();
    if (Object.keys(errors).length) {
      setErrors(errors);
      return;
    }
    setErrors({});
    dispatch(
      updateCompany({
        companyId,
        companyName,
        companyAddress,
        isAdmin,
        status,
        valve,
        mqttHost,
        mqttPort,
        mqttTopic,
        mqttPublish,
        navigate,
      })
    );
  };

  const handleLock = () => {
    dispatch(toggleCompanySupport());
  };

  return (
    <Container>
      <GridHeaderContainer container>
        <Grid xs={12} sm={5} item>
          <Path />
          <TitleDivContainer>
            <AvatarContainer>
              <BuildingOffice2 height={'72px'} width={'72px'} />
            </AvatarContainer>
            <TextContainer>
              <StyledBoldSpan>{companyName}</StyledBoldSpan>
              <StyledSpan>{companyAddress}</StyledSpan>
            </TextContainer>
          </TitleDivContainer>
        </Grid>
        <Grid
          xs={12}
          sm={5}
          item
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <ActionButton
            buttonLabel={t('button-update')}
            color={'primary'}
            OnClick={handleSendData}
            disabled={isUpdated}
            style={{
              borderRadius: 6,
              padding: '10px 16px 10px 16px',
              marginRight: 12,
            }}
          />
          {!isAdmin && (
            <ActionButton
              buttonLabel={
                companySupport ? t('company-lock') : t('company-unlock')
              }
              variant="outlined"
              color={'tertiary'}
              {...(companySupport
                ? { iconLeft: <LockedIcon /> }
                : { iconLeft: <UnlockedIcon /> })}
              OnClick={handleLock}
              style={{
                borderRadius: 6,
                padding: '10px 16px 10px 16px',
              }}
            />
          )}
        </Grid>
      </GridHeaderContainer>
      <GridContentContainer container>
        <Grid xs={12} sm={isAdmin ? 5 : 12} item>
          <Input
            label={t('company-name-label')}
            placeholder={t('company-name-placeholder')}
            value={companyName}
            hasBorder
            OnChange={handleChangeName}
            error={!!errors?.companyName}
            errorMessage={errors?.companyName}
          />
        </Grid>
        {isAdmin && (
          <Grid xs={12} sm={5} item>
            <SelectInput
              label={t('company-status-label')}
              placeholder={t('company-status-placeholder')}
              values={Statuses}
              value={status}
              OnChange={handleChangeStatus}
              error={!!errors?.status}
              errorMessage={errors?.status}
            />
          </Grid>
        )}
        <Grid xs={12} sm={isAdmin ? 5 : 12} item>
          <Input
            label={t('company-address-label')}
            placeholder={t('company-address-label')}
            value={companyAddress}
            hasBorder
            OnChange={handleChangeAddress}
            error={!!errors?.companyAddress}
            errorMessage={errors?.companyAddress}
          />
        </Grid>
        {isAdmin && (
          <Grid xs={12} sm={5} item>
            <SelectInput
              label={t('company-valve-label')}
              placeholder={t('company-status-placeholder')}
              values={activeLocale === 'en' ? StatusesValve : StatusesValveFR}
              value={valve}
              OnChange={handleChangeValve}
              error={!!errors?.valve}
              errorMessage={errors?.valve}
            />
          </Grid>
        )}
        {isAdmin && (
          <>
            <Grid xs={12} sm={isAdmin ? 5 : 12} item>
              <Input
                label={t('company-mqtt-host-label')}
                placeholder={t('company-address-label')}
                value={mqttHost}
                hasBorder
                OnChange={handleChangeHost}
                error={!!errors?.mqttHost}
                errorMessage={errors?.mqttHost}
              />
            </Grid>
            <Grid xs={12} sm={isAdmin ? 5 : 12} item>
              <Input
                label={t('company-mqtt-port-label')}
                placeholder={t('company-address-label')}
                value={mqttPort || 0}
                type="number"
                hasBorder
                OnChange={handleChangePort}
                error={!!errors?.mqttPort}
                errorMessage={errors?.mqttPort}
              />
            </Grid>
            <Grid xs={12} sm={isAdmin ? 5 : 12} item>
              <Input
                label={t('company-mqtt-topic-label')}
                placeholder={t('company-address-label')}
                value={mqttTopic}
                hasBorder
                OnChange={handleChangeTopic}
                error={!!errors?.mqttTopic}
                errorMessage={errors?.mqttTopic}
              />
            </Grid>

            <Grid xs={12} sm={5} item alignContent={'center'}>
              <PublishContainer>
                <PublishTitle>{t('company-mqtt-publish-label')}</PublishTitle>
                <SwitchStatus
                  checked={mqttPublish}
                  handleCheckedChange={() => {
                    setPublish((prev) => !prev);
                  }}
                />
              </PublishContainer>
            </Grid>
          </>
        )}
      </GridContentContainer>
    </Container>
  );
};

const Container = styled('div')`
  padding: 32px;
  width: -webkit-fill-available;
`;

const TitleDivContainer = styled('div')`
  display: flex;
  flex-direction: row;
`;

const AvatarContainer = styled('div')`
  display: flex;
  align-items: center;
  width: 72px;
  height: 72px;
  margin-right: 16px;
  background-color: #f0f0f0;
  border-radius: 50px;
  padding: 16px;
`;

const TextContainer = styled('div')`
  display: flex;
  flex-direction: column;
`;

const StyledBoldSpan = styled('span')`
  font-size: 24px;
  font-weight: 600;
  font-family: Rubik;
`;

const StyledSpan = styled('span')`
  font-size: 16px;
  font-weight: 300;
  color: #4a4a4a;
  font-family: Inter;
`;

const GridHeaderContainer = styled(Grid)(() => ({
  gap: 15,
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  marginBottom: 32,
}));

const GridContentContainer = styled(Grid)(() => ({
  gap: 15,
}));

const UnlockedIcon = styled(LockOpen)(() => ({
  width: 20,
  height: 20,
  marginRight: 6,
  stroke: ColorType.black,
}));

const LockedIcon = styled(LockClosed)(() => ({
  width: 20,
  height: 20,
  marginRight: 6,
  stroke: ColorType.black,
}));

const PublishContainer = styled(Grid)(() => ({
  display: 'flex',
  // flexDirection: 'column',
  gap: 12,
}));

const PublishTitle = styled('p')(() => ({
  letterSpacing: '0.03333em',
  textAlign: 'left',
  marginTop: '3px',
  marginRight: 0,
  marginBottom: 0,
  marginLeft: 0,
  fontFamily: 'Rubik !important',
  fontStyle: 'normal',
  fontWeight: '600 !important',
  fontSize: '14px !important',
  lineHeight: '22px !important',
  color: '#000 !important',
  paddingLeft: '5px !important',
  display: 'inline-flex',
}));

export default CompanyDetails;
